// Maps the `show_doc_answer` tool-call args (one call carries all per-doc
// answers in an `answers[]` array) into card view-models. Parsing/streaming
// helpers live in ./tool-args since they aren't doc-answer-specific.

import removeMarkdown from 'remove-markdown';
import { TAGS_KNOWLEDGE_CONTENT_TYPE_CLASSIFICATION } from '../../constants/tags.constants';
import type {
  ContentTypeClassification,
  KnowledgeContentType,
} from '../../constants/tags.constants';
import { AG_UI_TOOL } from '../agent/ag-ui.constants';
import { parseToolArgs } from '../agent/tool-args';

/** Structural slice of an AG-UI assistant message; avoids a hard @ag-ui dep. */
export interface AssistantMessageLike {
  content?: string | null;
  toolCalls?: { id: string; function: { name: string; arguments: string } }[];
}

/** One structured citation the answer carries in its `citations` array. */
export interface DocAnswerCitation {
  chunk_id?: string;
  document_id?: string;
}

/** Raw per-doc answer as it arrives in the `show_doc_answer` tool-call args. */
export interface DocAnswer {
  document_id?: string;
  context?: string | null;
  answer_text?: string;
  citations?: DocAnswerCitation[];
}

/** Card view-model rendered for a single per-doc answer. */
export interface DocAnswerCard {
  key: string;
  documentId: string;
  context: string | null;
  text: string;
  chunkIds: string[];
}

// A citation marker is a bracketed, comma-separated list of chunk ids (each
// optionally `chunk_id:`-labeled) where at least one id is a real chunk id — a
// 16+ hex run, required by the lookahead. That floor keeps mixed markers
// carrying a hallucinated short id (e.g. `[343536, <objectid>]`) getting
// stripped whole, while non-citation brackets ([decade], [123456], [table 2])
// are left intact.
const CITATION_MARKER =
  /\s*\[(?=[^\]]*[0-9a-f]{16})(?:chunk_id:\s*)?[0-9a-f]+(?:\s*,\s*(?:chunk_id:\s*)?[0-9a-f]+)*\]/gi;

// Chunk ids are 24-char hex ObjectIds; the model sometimes emits malformed or
// hallucinated ids, so every cited id is validated against this shape.
export const OBJECT_ID = /^[0-9a-f]{24}$/i;

/** Strips inline `[chunk_id]` citation markers from the streamed answer text. */
export function stripCitationMarkers(text = ''): string {
  return text.replace(CITATION_MARKER, '').trim();
}

/** The distinct, well-formed chunk ObjectIds an answer cites in its `citations`. */
function toCitedChunkIds(citations: DocAnswer['citations']): string[] {
  const list = Array.isArray(citations) ? citations : [];
  const ids = list
    .map(citation => citation?.chunk_id)
    .filter((id): id is string => typeof id === 'string' && OBJECT_ID.test(id));
  return [...new Set(ids)];
}

/** Minimal slice of a resolved knowledge item needed to attribute an answer. */
export interface AnswerSourceItem {
  title?: string;
  workspaceId?: string;
  tags?: { contentType?: KnowledgeContentType | null };
}

export interface ContentTypeChipView {
  contentType: KnowledgeContentType;
  classification: ContentTypeClassification;
}

/** The source's content-type chip; `null` (no chip) for a missing or unknown content type. */
export function toContentTypeChip(knowledgeItem: AnswerSourceItem): ContentTypeChipView | null {
  const contentType = knowledgeItem.tags?.contentType;
  const classification = contentType && TAGS_KNOWLEDGE_CONTENT_TYPE_CLASSIFICATION[contentType];
  return contentType && classification ? { contentType, classification } : null;
}

 /**
 * "Title (Workspace)" source attribution; the workspace is omitted when unknown.
 * @param knowledgeItem - Source item to format.
 * @param workspaceName - Optional workspace name.
 * @returns The formatted source attribution.
 * @example
 * toSourceName({ title: 'Clinical guideline' } as AnswerSourceItem, 'Cardiology');
 */
export function toSourceName(
  knowledgeItem: AnswerSourceItem,
  workspaceName?: string | null,
): string {
  return [knowledgeItem.title, workspaceName && `(${workspaceName})`].filter(Boolean).join(' ');
}

/**
 * Chunk content is markdown (zeus chunks the document's markdown); quotes render
 * it as one flat plain-text line so no raw markers or block layout leak through.
 */
export function toPlainQuote(content: string): string {
  return removeMarkdown(content).replace(/\s+/g, ' ').trim();
}

/** Word-boundary preview of a long quote; `null` when the whole text fits untruncated. */
export function toQuotePreview(text: string, maxLength: number): string | null {
  if (text.length <= maxLength) return null;
  const slice = text.slice(0, maxLength);
  const lastSpace = slice.lastIndexOf(' ');
  const preview = lastSpace > 0 ? slice.slice(0, lastSpace) : slice;
  // Drop trailing whitespace/punctuation so the ellipsis doesn't render as
  // "word,..." or "word....".
  return preview.replace(/[\s.,;:]+$/, '');
}

/** One entry of the `answers[]` array → a card view-model. */
export function toDocAnswerCard(answer: DocAnswer = {}, key: string): DocAnswerCard {
  return {
    key,
    documentId: answer.document_id ?? '',
    context: answer.context ?? null,
    text: answer.answer_text ?? '',
    chunkIds: toCitedChunkIds(answer.citations),
  };
}

/** Map a (possibly partial) parsed args object to one card per per-doc answer. */
export function toDocAnswerCards(
  parameters: { answers?: DocAnswer[] } = {},
  toolCallId: string,
): DocAnswerCard[] {
  const answers = Array.isArray(parameters.answers) ? parameters.answers : [];
  return answers.map((answer, index) => toDocAnswerCard(answer, `${toolCallId}-${index}`));
}

/**
 * All cards of an assistant message. Only `show_doc_answer` renders cards; one
 * call carries every per-doc answer, so we emit one card per `answers[]` entry.
 */
export function toMessageCards(message: AssistantMessageLike): DocAnswerCard[] {
  return (message.toolCalls || [])
    .filter(toolCall => toolCall.function?.name === AG_UI_TOOL.SHOW_DOC_ANSWER)
    .flatMap(toolCall => toDocAnswerCards(parseToolArgs(toolCall.function.arguments), toolCall.id));
}
